import CountDownPopup from './CountDownPopup.vue';
import moment from 'moment';
import {simplePopupFactory} from 'shared/simple_popups/simplePopupFactory';
import sessionExpireResourceImport from './session_expire_resource';
import { showDimmer, hideDimmer } from 'shared/utils/dimmer';
import { CurrentUserClass } from 'shared/CurrentUser';
import notifications from '~/vue/plugins/notifications';
import globalEventBus, { GLOBAL_EVENTS } from 'shared/globalEventBus';
import urls from 'DjangoUrls';

const { DjangoUrls } = urls;
const sessionExpireResource = sessionExpireResourceImport();
const SESSION_CHANNEL_NAME = 'multime-session';
const SESSION_CHANNEL_EVENTS = {
    EXTENDED: 'extended',
    LOGGED_OUT: 'logged-out',
};

window.moment=moment;
class SessionHandler {
    constructor () {
        this.coundDownPopupIsVisible = false;
        this.sessionExpireResource = sessionExpireResource;
        this.notifications = notifications;
        this.COUNTDOWN_TIME = 5 * 60;
        this.CHECK_PERIOD_TIME_SECONDS = 2 * 60;
    }

    /**
     * Called once, from the main bundle. Does nothing for anonymous visitors: they have no
     * session to keep alive.
     */
    start () {
        if (this.started || !new CurrentUserClass().is_authenticated()) {
            return;
        }
        this.started = true;
        this.bindVisibilityChangeListener();
        this.bindSessionChannelListener();
        this.scheduleSessionStatusCheck(this.CHECK_PERIOD_TIME_SECONDS);
    }

    bindVisibilityChangeListener () {
        globalEventBus.on(GLOBAL_EVENTS.TAB_VISIBILITY_CHANGED, isVisible => {
            if (isVisible) {
                this.checkSessionStatus();
            }
        });
    }

    bindSessionChannelListener () {
        if (typeof window.BroadcastChannel !== 'function') {
            return;
        }
        try {
            this.sessionChannel = new window.BroadcastChannel(SESSION_CHANNEL_NAME);
            this.sessionChannel.addEventListener('message', event => {
                if (event.data?.type === SESSION_CHANNEL_EVENTS.EXTENDED) {
                    this.checkSessionStatus();
                } else if (event.data?.type === SESSION_CHANNEL_EVENTS.LOGGED_OUT) {
                    clearTimeout(this._statusCheckTimeout);
                    this.refreshPage();
                }
            });
        } catch (e) {
            this.sessionChannel = undefined;
        }
    }

    broadcastSessionEvent (type) {
        try {
            this.sessionChannel?.postMessage({type});
        } catch (e) {
            // Visibility checks still reconcile session state if the channel is unavailable.
        }
    }

    scheduleSessionStatusCheck (secondsUntilCheck) {
        clearTimeout(this._statusCheckTimeout);
        this._statusCheckTimeout = setTimeout(() => {
            this._statusCheckTimeout = undefined;
            this.checkSessionStatus();
        }, Math.max(secondsUntilCheck, 0) * 1000);
    }

    async checkSessionStatus () {
        clearTimeout(this._statusCheckTimeout);
        this._statusCheckTimeout = undefined;
        try {
            const resp = await this.sessionExpireResource.get_session_status();
            let {
                session_active,
                seconds_before_expiration
            } = resp.data;
            if (!session_active) {
                this.broadcastSessionEvent(SESSION_CHANNEL_EVENTS.LOGGED_OUT);
                this.refreshPage();
            } else if (seconds_before_expiration <= this.COUNTDOWN_TIME) {
                if (!this.coundDownPopupIsVisible) {
                    this.showCountDownPopup(seconds_before_expiration).catch(() => {
                    });
                }
            } else {
                if (this.coundDownPopupIsVisible) {
                    this.closeCountDownPopup().catch(() => {
                    });
                }
                this.scheduleSessionStatusCheck(
                    seconds_before_expiration - this.COUNTDOWN_TIME
                );
            }
        } catch (e) {
            this.notifications.error(e);
            this.scheduleSessionStatusCheck(this.CHECK_PERIOD_TIME_SECONDS);
        }
    }

    async showCountDownPopup (secondsBeforeSessionExpire) {
        this.coundDownPopupIsVisible = true;
        const {
            popupComponent,
            fEl,
            vm
        } = simplePopupFactory.mount_vue_popup(CountDownPopup);
        this.popupComponent = popupComponent;
        const res = await popupComponent.show(secondsBeforeSessionExpire).catch(() => {
        });
        this.coundDownPopupIsVisible = false;
        vm?.$destroy();
        fEl?.focus();
        if (res) {
            this.tryToPreventExpiration(false).catch(() => {
            });
        } else {
            this.scheduleSessionStatusCheck(1);
        }
    }

    async closeCountDownPopup () {
        this.popupComponent.close();
    }

    refreshPage () {
        document.location.reload();
    }

    async tryToPreventExpiration (silent = false) {
        if (!silent) {
            showDimmer();
        }
        try {
            const resp = await this.sessionExpireResource.session_prevent_expire();
            const {
                session_active,
                seconds_before_expiration
            } = resp.data;
            if (!session_active) {
                this.broadcastSessionEvent(SESSION_CHANNEL_EVENTS.LOGGED_OUT);
                this.refreshPage();
            } else if (seconds_before_expiration > 2) {
                hideDimmer();
                this.scheduleSessionStatusCheck(
                    seconds_before_expiration - this.COUNTDOWN_TIME
                );
                this.broadcastSessionEvent(SESSION_CHANNEL_EVENTS.EXTENDED);
                if (!silent) {
                    this.notifications.success('Session successfully continued');
                }
            } else {
                this.refreshPage();
            }
        } catch (e) {
            this.notifications.error(e);
            this.scheduleSessionStatusCheck(1);
        }
    }

    async logout (logoutUrl) {
        if (this._logoutInProgress) {
            return;
        }
        this._logoutInProgress = true;
        try {
            await this.sessionExpireResource.logout(logoutUrl);
        } catch (e) {
            // a failed request can still mean logged out, e.g. the idle check expired the session
            if (await this.sessionIsActive()) {
                this._logoutInProgress = false;
                this.notifications.error('Could not log out. Please try again.');
                return;
            }
        }
        this.broadcastSessionEvent(SESSION_CHANNEL_EVENTS.LOGGED_OUT);
        this.navigate(DjangoUrls['auth_logged_out']());
    }

    async sessionIsActive () {
        try {
            const resp = await this.sessionExpireResource.get_session_status();
            return resp.data.session_active;
        } catch (e) {
            return true;
        }
    }

    navigate (url) {
        document.location.assign(url);
    }

    start_session_auto_support () {
        this._support_interval = setInterval(() => {
            this.tryToPreventExpiration.call(this, [true]);
        }, 2 * 60 * 1000);
    }

    stop_session_auto_support () {
        if (this._support_interval !== undefined) {
            clearInterval(this._support_interval);
        }
    }

}

// angular kept a single instance for the whole page; webpack copies this module into every entry
// bundle, so the instance is shared through `window` (see shared/globalEventBus for the details).
const GLOBAL_KEY = '__mmSessionHandler__';
const sessionHandler = window[GLOBAL_KEY] || (window[GLOBAL_KEY] = new SessionHandler());

export default sessionHandler;
export {sessionHandler, SessionHandler, SESSION_CHANNEL_EVENTS, SESSION_CHANNEL_NAME};
